import i18n from "@i18n/i18n";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import {
  useCreateBenchmarkCatalog,
  useBenchmarkCatalog,
  useUpdateBenchmarkCatalog,
} from "@api/benchmarkCatalog";
import {
  BenchmarkCatalogCreateRequest,
  BenchmarkCatalogDto,
  BenchmarkCatalogUpdateRequest,
  BenchmarkCatalogValueInput,
} from "@api/apiClient";
import { useBenchmarkModelTypes } from "@api/benchmarkModelTypes";
import { MetadataEntityResponse } from "@api/apiClient";

interface BenchmarkCatalogDialogProps {
  open: boolean;
  /** When provided the dialog is in edit mode; otherwise create mode. */
  editCatalogId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface RowState {
  scope: "WholeBuilding" | "ModelType";
  modelTypeEntityId?: string;
  modelTypeName?: string;
  value: string;
  isNew: boolean;
}

function numericValue(s: string): number {
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

const BenchmarkCatalogDialog = ({
  open,
  editCatalogId,
  onClose,
  onSaved,
}: BenchmarkCatalogDialogProps) => {
  const theme = useTheme();
  const isEdit = !!editCatalogId;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rows, setRows] = useState<RowState[]>([]);
  const [nameError, setNameError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  // Load existing catalog for edit mode
  const { data: catalog, isFetching: catalogLoading } = useBenchmarkCatalog(
    editCatalogId,
    { enabled: isEdit && open }
  );

  // Always load BenchmarkModelTypes so we can show dynamic rows
  const { data: modelTypes = [] } = useBenchmarkModelTypes({ enabled: open });

  const createMutation = useCreateBenchmarkCatalog({
    onSuccess: () => { onSaved(); onClose(); },
  });

  const updateMutation = useUpdateBenchmarkCatalog({
    onSuccess: () => { onSaved(); onClose(); },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Build rows whenever the dialog opens or the catalog / model-types load
  useEffect(() => {
    if (!open) return;

    if (!isEdit) {
      // Create mode: one WholeBuilding row + one row per model type
      setName("");
      setDescription("");
      setRows(buildRows(null, modelTypes));
      setNameError("");
      return;
    }

    if (catalog) {
      setName(catalog.name ?? "");
      setDescription(catalog.description ?? "");
      setRows(buildRows(catalog, modelTypes));
      setNameError("");
    }
  }, [open, catalog, modelTypes, isEdit]);

  function buildRows(
    existing: BenchmarkCatalogDto | null,
    mts: MetadataEntityResponse[]
  ): RowState[] {
    const result: RowState[] = [];

    // Whole Building row
    const wbValue = existing?.values?.find((v) => v.scope === "WholeBuilding");
    result.push({
      scope: "WholeBuilding",
      value: wbValue ? String(wbValue.valueKgCo2ePerM2) : "",
      isNew: !wbValue,
    });

    // One row per model type — skip entries whose id is undefined (generated client can return undefined)
    for (const mt of mts) {
      if (!mt.metadataEntityId) continue;
      const existing_val = existing?.values?.find(
        (v) => v.scope === "ModelType" && v.modelTypeEntityId === mt.metadataEntityId
      );
      result.push({
        scope: "ModelType",
        modelTypeEntityId: mt.metadataEntityId,
        modelTypeName: mt.name ?? "",
        value: existing_val ? String(existing_val.valueKgCo2ePerM2) : "",
        isNew: !existing_val,
      });
    }

    return result;
  }

  const handleValueChange = (index: number, val: string) => {
    // Only allow numeric input (digits, dot, optional leading minus)
    if (val !== "" && !/^-?\d*\.?\d*$/.test(val)) return;
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, value: val } : r))
    );
  };

  const performSave = async () => {
    const trimmedName = name.trim();
    const values = rows
      .filter((r) => r.value.trim() !== "")
      .map(
        (r) =>
          new BenchmarkCatalogValueInput({
            scope: r.scope,
            modelTypeEntityId: r.modelTypeEntityId,
            valueKgCo2ePerM2: numericValue(r.value),
          })
      );

    if (isEdit && editCatalogId) {
      await updateMutation.mutateAsync({
        id: editCatalogId,
        request: new BenchmarkCatalogUpdateRequest({ name: trimmedName, description, values }),
      });
    } else {
      await createMutation.mutateAsync(
        new BenchmarkCatalogCreateRequest({ name: trimmedName, description, values })
      );
    }
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(i18n.t("benchmark-catalog-dialog.validation.name-required"));
      return;
    }
    setNameError("");

    if (isEdit) {
      setShowConfirm(true);
      return;
    }

    await performSave();
  };

  const handleConfirmProceed = async () => {
    setShowConfirm(false);
    await performSave();
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const mutationError =
    (createMutation.error as any)?.response?.data?.error ||
    (updateMutation.error as any)?.response?.data?.error;

  return (
    <>
    <Dialog open={open && !showConfirm} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {isEdit
          ? i18n.t("benchmark-catalog-dialog.title-edit")
          : i18n.t("benchmark-catalog-dialog.title-create")}
        <IconButton aria-label="close" onClick={handleClose} disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {catalogLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* Name & Description */}
            <TextField
              label={i18n.t("benchmark-catalog-dialog.name-label")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError("");
              }}
              error={!!nameError}
              helperText={nameError}
              fullWidth
              variant="outlined"
              required
              disabled={isSaving}
            />
            <TextField
              label={i18n.t("benchmark-catalog-dialog.description-label")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              disabled={isSaving}
            />

            <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
              <CardHeader
                title={
                  <Typography variant="h6" fontWeight="bold">
                    {i18n.t("benchmark-catalog-dialog.values-section-label")}
                  </Typography>
                }
              />
              <CardContent sx={{ borderTop: 1, borderColor: theme.palette.divider }}>
                {rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center">
                    {i18n.t("benchmark-catalog-dialog.no-model-types")}
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {rows.map((row, idx) => (
                      <Grid
                        key={`${row.scope}-${row.modelTypeEntityId ?? "wb"}`}
                        size={row.scope === "WholeBuilding" ? 12 : 6}
                      >
                        <TextField
                          label={
                            row.scope === "WholeBuilding"
                              ? i18n.t("benchmark-catalog-dialog.scope-whole-building")
                              : row.modelTypeName
                          }
                          value={row.value}
                          onChange={(e) => handleValueChange(idx, e.target.value)}
                          placeholder="0"
                          variant="outlined"
                          fullWidth
                          disabled={isSaving}
                          helperText={
                            row.isNew
                              ? i18n.t("benchmark-catalog-dialog.new-badge")
                              : undefined
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>

            {mutationError && (
              <Typography color="error" variant="body2">
                {mutationError}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={isSaving}>
          {i18n.t("benchmark-catalog-dialog.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || catalogLoading}
        >
          {isSaving
            ? i18n.t("benchmark-catalog-dialog.saving")
            : i18n.t("benchmark-catalog-dialog.save")}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.confirm-title")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{i18n.t("rebar-material-grid.edit-benchmark-catalog-confirm")}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowConfirm(false)} variant="outlined">
          {i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.cancel")}
        </Button>
        <Button onClick={handleConfirmProceed} variant="contained" loading={isSaving}>
          {i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.confirm-proceed")}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default BenchmarkCatalogDialog;
